import { PageFrame } from "../../../../../../shared/frontend/layout/PageFrame";
import { useFormDirty } from "../../app/utils/useFormDirty";
import { typography as designTypography, drawerDefaults, spacing } from "../../app/design-system";
import { CheckOutlined,CloseOutlined,FileTextOutlined,InfoCircleOutlined,ReloadOutlined } from "@ant-design/icons";
import { App,Button,Form,Input,Select,Space,Tabs,Tag,Typography } from "../../../../../../shared/frontend/ui";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../app/auth/useAuth";
import { Permissions } from "../../app/constants/permissions";
import { useChannelAvailability, type ChannelFlags } from "../../app/integrations/useChannelAvailability";
import { useI18n } from "../../app/i18n/useI18n";
import { useRealtimeListRefresh } from "../../app/realtime/RealtimeContext";
import { RealtimeEventTypes } from "../../app/realtime/realtimeEventTypes";
import { useGlobalStudioContext } from "../../app/studios/useGlobalStudioContext";
import { formatDateTimeNoSeconds } from "../../app/units/format";
import { getActiveRequestSort } from "../../app/utils/proTableSortPersistence";
import { loadUnits } from "../../app/units/unitsStorage";
import { FormDrawer } from "../../components/FormDrawer";
import {
SettingsListPage,
type ColumnDefinition,
type ProColumnsWithMeasure,
type SettingsListActionRef, type SettingsListRequest } from "../../components/SettingsListPage";
import { listActionPreset, makeActionColumn, type ListActionDefinition } from "../../components/ui/antd";
import { letterProviderRouteLabel } from "./letterProviderRouteLabel";
import { OutboundMessageDetails } from "./OutboundMessageDetails";
import { OutboundMessageDocument } from "./OutboundMessageDocument";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";

const outboundMessagesActionSlotCounts = {
  actionsColumn: 2,
} as const;

const outboundMessagesColumnWidths = {
  status: 140,
  letterRoute: 200,
  sourceType: 140,
  studioName: 160,
  createdAt: 170,
  actionsColumn: 210,
} as const;

type OutboundMessageDto = {
  id: string;
  studioId?: string | null;
  studioName?: string | null;
  personId?: string | null;
  channel: string;
  sourceType: string;
  sourceId?: string | null;
  status: string;
  providerIntegrationId?: string | null;
  providerIntegrationDisplayName?: string | null;
  providerIntegrationType?: string | null;
  recipientName?: string | null;
  recipientEmail?: string | null;
  recipientPhone?: string | null;
  subject?: string | null;
  body?: string | null;
  bodyFormat?: string | null;
  documentTitle?: string | null;
  attachmentObjectKey?: string | null;
  attachmentFileName?: string | null;
  payloadJson?: string | null;
  technicalQueueType?: string | null;
  technicalQueueId?: string | null;
  externalMessageId?: string | null;
  errorMessage?: string | null;
  createdByAccountId?: string | null;
  approvedByAccountId?: string | null;
  approvedAt?: string | null;
  queuedAt?: string | null;
  sentAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdByDisplayName?: string | null;
  retryCount?: number | null;
  lastRetriedAt?: string | null;
  cancelledAt?: string | null;
  cancelReason?: string | null;
};

type ListResponse = { total: number; data: OutboundMessageDto[] };

const OUTBOUND_STATUS = {
  PendingApproval: "PendingApproval",
  Queued: "Queued",
  Sending: "Sending",
  Sent: "Sent",
  Failed: "Failed",
  Cancelled: "Cancelled",
} as const;

const OUTBOUND_CHANNEL = {
  Email: "Email",
  Letter: "Letter",
  Sms: "Sms",
  WhatsApp: "WhatsApp",
} as const;

const DEFAULT_PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "", labelKey: "All statuses" },
  { value: OUTBOUND_STATUS.PendingApproval, labelKey: "Pending approval" },
  { value: OUTBOUND_STATUS.Queued, labelKey: "Queued" },
  { value: OUTBOUND_STATUS.Sending, labelKey: "Sending" },
  { value: OUTBOUND_STATUS.Sent, labelKey: "Sent" },
  { value: OUTBOUND_STATUS.Failed, labelKey: "Failed" },
  { value: OUTBOUND_STATUS.Cancelled, labelKey: "Cancelled" },
] as const;

// Reihenfolge wie im Posteingang (E-Mail, Brief, SMS, WhatsApp). Die Schlüssel sind API-Werte.
const OUTBOUND_CHANNELS = [OUTBOUND_CHANNEL.Email, OUTBOUND_CHANNEL.Letter, OUTBOUND_CHANNEL.Sms, OUTBOUND_CHANNEL.WhatsApp] as const;
type OutboundChannelTab = (typeof OUTBOUND_CHANNELS)[number];
const OUTBOUND_MESSAGE_EDIT_FORM_ID = "outbound-message-edit-form";

function isOutboundChannel(t: string): t is OutboundChannelTab {
  return (OUTBOUND_CHANNELS as readonly string[]).includes(t);
}

/** Brief, SMS und WhatsApp gibt es nur mit Integration oder vorhandenem Bestand. */
function isOutboundChannelVisible(ch: OutboundChannelTab, visible: ChannelFlags): boolean {
  switch (ch) {
    case "Letter":
      return visible.letter;
    case "Sms":
      return visible.sms;
    case "WhatsApp":
      return visible.whatsApp;
    default:
      return true;
  }
}

function outboundChannelTabLabel(ch: OutboundChannelTab, text: (key: string) => string): string {
  switch (ch) {
    case "Email":
      return text("Email");
    case "Sms":
      return text("SMS");
    case "WhatsApp":
      return text("WhatsApp");
    case "Letter":
      return text("Letter");
    default:
      return ch;
  }
}

function resolveMainTab(
  raw: string | null,
  canOutboundList: boolean,
  visible: ChannelFlags
): OutboundChannelTab {
  const t = (raw ?? "").trim();
  if (t && isOutboundChannel(t) && canOutboundList && isOutboundChannelVisible(t, visible)) return t;
  return "Email";
}

function statusColor(s: string | null | undefined): string {
  const v = (s || "").toLowerCase();
  if (v.includes("pending")) return "gold";
  if (v.includes("failed")) return "red";
  if (v.includes("sent")) return "green";
  if (v.includes("sending")) return "blue";
  if (v.includes("queued")) return "cyan";
  if (v.includes("cancel")) return "default";
  return "default";
}

export function OutboundMessagesPage() {
  const auth = useAuth();
  const { text } = useI18n();
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const { studioIdsQueryParam } = useGlobalStudioContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const units = React.useMemo(() => loadUnits(auth.info?.accountId), [auth.info?.accountId]);
  const actionRef = React.useRef<SettingsListActionRef>(null);
  useRealtimeListRefresh({
    eventTypes: [RealtimeEventTypes.OutboundMessagesChanged, RealtimeEventTypes.EmailQueueChanged, RealtimeEventTypes.SmsQueueChanged, RealtimeEventTypes.WhatsAppQueueChanged],
    reload: () => void actionRef.current?.revalidateCurrentQuery(),
    enabled: !!auth.token
  });

  const canOutboundList = auth.hasAnyPermission(
    Permissions.CommunicationOutboundRead,
    Permissions.CommunicationOutboundReview,
    "*"
  );

  const canRead = canOutboundList;
  const canWrite = auth.hasAnyPermission(Permissions.CommunicationOutboundWrite, "*");
  const canReview = auth.hasAnyPermission(Permissions.CommunicationOutboundReview, "*");
  const canReviewOwn = auth.hasAnyPermission(Permissions.CommunicationOutboundReviewOwn, "*");
  const canCancel = auth.hasAnyPermission(Permissions.CommunicationOutboundCancel, "*");
  const canRetry = auth.hasAnyPermission(Permissions.CommunicationOutboundRetry, "*");

  const tabFromUrl = searchParams.get("tab");

  const channelAvailability = useChannelAvailability();
  const mainTab = resolveMainTab(tabFromUrl, canOutboundList, channelAvailability.isVisible);

  const setMainTab = (key: string) => {
    setSearchParams({ tab: key });
  };

  const [filterStatus, setFilterStatus] = React.useState<string>("");

  /**
   * Welche Frage der Drawer beantwortet: „was stand drin" oder „wie ist es gelaufen". Beides in
   * einem Rohformular zu zeigen hiess, dass keine der beiden Fragen eine gute Antwort bekam.
   */
  const [drawerMode, setDrawerMode] = React.useState<"document" | "details" | null>(null);
  const drawerOpen = drawerMode != null;
  const [drawerLoading, setDrawerLoading] = React.useState(false);
  const [drawerSubmitting, setDrawerSubmitting] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<OutboundMessageDto | null>(null);
  const [editForm] = Form.useForm();
  const { dirty: drawerDirty, initialize: initializeDrawer } = useFormDirty(editForm);

  React.useEffect(() => {
    void actionRef.current?.revalidateCurrentQuery();
  }, [filterStatus, studioIdsQueryParam, mainTab]);

  const loadById = React.useCallback(
    async (id: string) => {
      setDrawerLoading(true);
      try {
        const row = await auth.api.get<OutboundMessageDto>(`/api/v1/outbound-messages/${id}`);
        setSelected(row);
        initializeDrawer({
          recipientName: row.recipientName,
          recipientEmail: row.recipientEmail,
          recipientPhone: row.recipientPhone,
          subject: row.subject,
          body: row.body,
          bodyFormat: row.bodyFormat,
          documentTitle: row.documentTitle,
          attachmentObjectKey: row.attachmentObjectKey,
          attachmentFileName: row.attachmentFileName,
          payloadJson: row.payloadJson,
          providerIntegrationId: row.providerIntegrationId,
        });
      } finally {
        setDrawerLoading(false);
      }
    },
    [auth.api, initializeDrawer]
  );

  const openDrawer = React.useCallback(
    async (id: string, mode: "document" | "details") => {
      setSelectedId(id);
      setDrawerMode(mode);
      await loadById(id);
    },
    [loadById]
  );

  const closeDrawer = () => {
    setDrawerMode(null);
    setSelectedId(null);
    setSelected(null);
    editForm.resetFields();
  };

  const approve = async () => {
    if (!selectedId) return;
    modal.confirm({
      title: text("Approve dispatch"),
      content: text("Do you want to approve this dispatch now?"),
      okText: text("Approve"),
      cancelText: text("Cancel"),
      onOk: async () => {
        setDrawerSubmitting(true);
        try {
          await auth.api.post(`/api/v1/outbound-messages/${selectedId}/approve`, {});
          feedback.success(text("Approved"));
          closeDrawer();
          void actionRef.current?.revalidateCurrentQuery();
        } finally {
          setDrawerSubmitting(false);
        }
      },
    });
  };

  const cancel = async () => {
    if (!selectedId) return;
    modal.confirm({
      title: text("Cancel dispatch"),
      content: text("Do you want to cancel this dispatch?"),
      okText: text("Cancel dispatch"),
      cancelText: text("Back"),
      okButtonProps: { danger: true },
      onOk: async () => {
        setDrawerSubmitting(true);
        try {
          await auth.api.post(`/api/v1/outbound-messages/${selectedId}/cancel`, { reason: null });
          feedback.success(text("Dispatch cancelled"));
          closeDrawer();
          void actionRef.current?.revalidateCurrentQuery();
        } finally {
          setDrawerSubmitting(false);
        }
      },
    });
  };

  const retry = async () => {
    if (!selectedId) return;
    setDrawerSubmitting(true);
    try {
      await auth.api.post(`/api/v1/outbound-messages/${selectedId}/retry`, {});
      feedback.success(text("Retry scheduled"));
      closeDrawer();
      void actionRef.current?.revalidateCurrentQuery();
    } finally {
      setDrawerSubmitting(false);
    }
  };

  const saveEdits = async (values: Record<string, unknown>) => {
    if (!selectedId) return;
    setDrawerSubmitting(true);
    try {
      await auth.api.put(`/api/v1/outbound-messages/${selectedId}`, values);
      feedback.success(text("Saved"));
      closeDrawer();
      void actionRef.current?.revalidateCurrentQuery();
    } finally {
      setDrawerSubmitting(false);
    }
  };

  const activeOutboundChannel: OutboundChannelTab = mainTab;

  const columnDefinitions: ColumnDefinition[] = React.useMemo(() => {
    const letterCol: ColumnDefinition[] =
      mainTab === "Letter"
        ? [{ key: "letterRoute", label: "Inbox letter route", defaultVisible: true }]
        : [];
    return [
      { key: "status", label: "Status", defaultVisible: true },
      ...letterCol,
      { key: "sourceType", label: "Source", defaultVisible: true },
      { key: "recipient", label: "Recipient", defaultVisible: true },
      { key: "subject", label: "Subject", defaultVisible: true },
      { key: "studioName", label: "Studio", defaultVisible: true },
      { key: "createdAt", label: "Created", defaultVisible: true },
      { key: "error", label: "Error", defaultVisible: false },
    ];
  }, [mainTab]);

  const allColumnsMap: Record<string, ProColumnsWithMeasure<OutboundMessageDto>> = React.useMemo(() => {
    const statusCol: ProColumnsWithMeasure<OutboundMessageDto> = {
      title: text("Status"),
      key: "status",
      dataIndex: "status",
      width: outboundMessagesColumnWidths.status,
      sorter: true,
      render: (_, r) => <Tag color={statusColor(r.status)}>{r.status}</Tag>,
    };
    const letterRouteCol: ProColumnsWithMeasure<OutboundMessageDto> = {
      title: text("Inbox letter route"),
      key: "letterRoute",
      width: outboundMessagesColumnWidths.letterRoute,
      ellipsis: true,
      sorter: true,
      sortValue: (r) => letterProviderRouteLabel(r.providerIntegrationDisplayName, r.providerIntegrationType, text),
      render: (_, r) =>
        letterProviderRouteLabel(r.providerIntegrationDisplayName, r.providerIntegrationType, text),
    };
    const rest: Record<string, ProColumnsWithMeasure<OutboundMessageDto>> = {
      sourceType: {
        title: text("Source"),
        key: "sourceType",
        dataIndex: "sourceType",
        width: outboundMessagesColumnWidths.sourceType,
        sorter: true,
      },
      recipient: {
        title: text("Recipient"),
        key: "recipient",
        ellipsis: true,
        sorter: true,
        sortValue: (r) => `${r.recipientName || ""} ${r.recipientEmail || r.recipientPhone || ""}`.trim(),
        render: (_, r) => {
          const name = r.recipientName || text("—");
          const addr = r.recipientEmail || r.recipientPhone || text("—");
          return (
            <div style={{ lineHeight: designTypography.lineHeight.tight }}>
              <div style={{ fontWeight: 500 }}>{name}</div>
              <Typography.Text type="secondary">{addr}</Typography.Text>
            </div>
          );
        },
      },
      subject: {
        title: text("Subject"),
        key: "subject",
        dataIndex: "subject",
        ellipsis: true,
        sorter: true,
        render: (_, r) => r.subject || r.documentTitle || text("—"),
      },
      studioName: {
        title: text("Studio"),
        key: "studioName",
        dataIndex: "studioName",
        width: outboundMessagesColumnWidths.studioName,
        sorter: true,
        render: (_, r) => r.studioName || text("—"),
      },
      createdAt: {
        title: text("Created"),
        key: "createdAt",
        dataIndex: "createdAt",
        width: outboundMessagesColumnWidths.createdAt,
        sorter: true,
        defaultSortOrder: "descend",
        render: (_, r) =>
          r.createdAt ? formatDateTimeNoSeconds(r.createdAt, units) : text("—"),
      },
      error: {
        title: text("Error"),
        key: "error",
        dataIndex: "errorMessage",
        ellipsis: true,
        sorter: true,
        render: (_, r) =>
          r.errorMessage ? (
            <Typography.Text type="danger">{r.errorMessage}</Typography.Text>
          ) : (
            text("—")
          ),
      },
    };
    if (mainTab === "Letter") {
      return { status: statusCol, letterRoute: letterRouteCol, ...rest };
    }
    return { status: statusCol, ...rest };
  }, [mainTab, text, units]);

  // Aktionen über den Wrapper (rechts fixiert, außerhalb des Spalten-Editors) — keine Datenspalte.
  const actionsColumn = React.useMemo(
    () => makeActionColumn<OutboundMessageDto>({ width: outboundMessagesColumnWidths.actionsColumn, slotCount: outboundMessagesActionSlotCounts.actionsColumn }),
    []
  );
  const rowActions = React.useMemo<ListActionDefinition<OutboundMessageDto>[]>(() => [
    listActionPreset<OutboundMessageDto>("view", {
      key: "document",
      label: "Document",
      icon: <FileTextOutlined />,
      onClick: (r) => openDrawer(r.id, "document"),
    }),
    listActionPreset<OutboundMessageDto>("details", {
      icon: <InfoCircleOutlined />,
      onClick: (r) => openDrawer(r.id, "details"),
    }),
  ], [openDrawer]);

  const tabBarItems = React.useMemo(() => {
    const items: { key: string; label: string }[] = [];
    if (canOutboundList) {
      for (const ch of OUTBOUND_CHANNELS) {
        if (!isOutboundChannelVisible(ch, channelAvailability.isVisible)) continue;
        items.push({ key: ch, label: outboundChannelTabLabel(ch, text) });
      }
    }
    return items;
  }, [canOutboundList, channelAvailability.isVisible, text]);

  const requestMessages = React.useCallback<SettingsListRequest<OutboundMessageDto>>(async (params, sort) => {
    const pageSize = params?.pageSize ?? DEFAULT_PAGE_SIZE;
    const current = params?.current ?? 1;
    const skip = (current - 1) * pageSize;
    const qp = new URLSearchParams();
    if (filterStatus) qp.set("status", filterStatus);
    qp.set("channel", activeOutboundChannel);
    if (studioIdsQueryParam) qp.set("studioIds", studioIdsQueryParam);
    qp.set("skip", String(skip));
    qp.set("take", String(pageSize));
    const activeSort = getActiveRequestSort(sort);
    if (activeSort) {
      qp.set("sortBy", activeSort.field);
      qp.set("sortOrder", activeSort.direction);
    }
    const res = await auth.api.get<ListResponse>(`/api/v1/outbound-messages?${qp.toString()}`);
    return { data: res.data || [], total: res.total || 0, success: true };
  }, [auth.api, filterStatus, activeOutboundChannel, studioIdsQueryParam]);

  if (!canRead) {
    return (
      <PageFrame title={text("Dispatch")}><div><Typography.Text type="secondary">{text("No permissions available")}</Typography.Text></div></PageFrame>
    );
  }

  return (
    <PageFrame title={text("Dispatch")}><div>{/* Ohne Schnittstelle bleibt nur E-Mail — dann braucht es keine Reiterleiste. */}{tabBarItems.length > 1 ? (
        <Tabs
          className="communication-outbound-tabs"
          activeKey={mainTab}
          onChange={setMainTab}
          items={tabBarItems.map((it) => ({ key: it.key, label: it.label }))}
          style={{ marginBottom: spacing.elementGap }}
        />
      ) : null}<SettingsListPage<OutboundMessageDto>
        dataMode="server-query"
        storageKey={`communication.outbound.list.${activeOutboundChannel.toLowerCase()}`}
        rowKey="id"
        actionRef={actionRef}
        request={requestMessages}
        columnDefinitions={columnDefinitions}
        allColumnsMap={allColumnsMap}
        toolbarExtra={
          <Space wrap>
            <Select
              style={{ minWidth: 180 }}
              value={filterStatus}
              onChange={(v) => setFilterStatus(v)}
              options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: text(o.labelKey) }))}
            />
          </Space>
        }
        onRowClick={(r) => void openDrawer(r.id, "document")}
        actionsColumn={actionsColumn}
        actions={rowActions}
        toolBarActions={[]}
        sectionTitle={null}
      /><FormDrawer mode={drawerMode === "details" && selected?.status === OUTBOUND_STATUS.PendingApproval && canWrite ? "edit" : "view"} dirty={drawerDirty}
        title={drawerMode === "details" ? "Dispatch details" : "Document"}
        open={drawerOpen}
        onCancel={closeDrawer}
        width={drawerDefaults.wideWidth}
        contentLoading={drawerLoading}
        submitting={drawerSubmitting}
        formId={drawerMode === "details" && selected?.status === OUTBOUND_STATUS.PendingApproval && canWrite
          ? OUTBOUND_MESSAGE_EDIT_FORM_ID
          : undefined}
        submitText="Save"
        hideSubmit={drawerMode !== "details" || selected?.status !== OUTBOUND_STATUS.PendingApproval || !canWrite}

        autoFocusFirstField={drawerMode === "details"}
        extra={
          drawerMode === "details" ? (
            <Space>
              {selected?.status === OUTBOUND_STATUS.PendingApproval && canReview ? (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  disabled={
                    !canReview ||
                    Boolean(!canReviewOwn && selected?.createdByAccountId && selected.createdByAccountId === auth.info?.accountId)
                  }
                  onClick={approve}
                >
                  {text("Approve")}
                </Button>
              ) : null}
              {selected?.status === OUTBOUND_STATUS.PendingApproval && canCancel ? (
                <Button danger icon={<CloseOutlined />} onClick={cancel}>
                  {text("Cancel")}
                </Button>
              ) : null}
              {selected?.status === OUTBOUND_STATUS.Failed && canRetry ? (
                <Button icon={<ReloadOutlined />} onClick={retry}>
                  {text("Retry")}
                </Button>
              ) : null}
            </Space>
          ) : null
        }
      >
        {!selected ? null : drawerMode === "document" ? (
            <OutboundMessageDocument
              api={auth.api}
              messageId={selected.id}
              body={selected.body}
              bodyFormat={selected.bodyFormat}
              attachmentFileName={selected.attachmentFileName}
              hasAttachment={Boolean(selected.attachmentObjectKey)}
              text={text}
            />
          ) : (
            <Space direction="vertical" style={{ width: "100%" }} size={spacing.elementGap}>
              <OutboundMessageDetails row={selected} units={units} text={text} statusColor={statusColor} />

              {/*
                Vor der Freigabe darf korrigiert werden — das ist der Zweck der Vier-Augen-Prüfung.
                Die technischen Felder bleiben verdeckt im Formular: Das Backend schreibt beim
                Speichern jedes Feld des Datensatzes, ein weggelassenes wuerde geleert.
              */}
              {selected.status === OUTBOUND_STATUS.PendingApproval && canWrite ? (
                <Form
                  id={OUTBOUND_MESSAGE_EDIT_FORM_ID}
                  form={editForm}
                  layout="vertical"
                  onFinish={saveEdits}
                >
                  <Form.Item name="recipientName" label={text("Recipient name")}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="recipientEmail" label={text("Email")} hidden={selected.channel !== OUTBOUND_CHANNEL.Email}>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="recipientPhone"
                    label={text("Phone")}
                    hidden={selected.channel !== OUTBOUND_CHANNEL.Sms && selected.channel !== OUTBOUND_CHANNEL.WhatsApp}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item name="subject" label={text("Subject")}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="body" label={text("Body")}>
                    <Input.TextArea rows={8} />
                  </Form.Item>
                  <Form.Item name="bodyFormat" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="documentTitle" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="attachmentObjectKey" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="attachmentFileName" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="payloadJson" hidden>
                    <Input />
                  </Form.Item>
                </Form>
              ) : null}
            </Space>
          )}
      </FormDrawer></div></PageFrame>
  );
}
